/**
 * Dashboard Component
 *
 * Displays a list of franchises associated with the logged-in user.
 * Fetches franchises from session storage or API and provides navigation to franchise details.
 */
import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Alert,
} from "@mui/material"

import { container } from "../../infrastructure/container"
import { Logger } from "../../infrastructure/services/logger"
import { SessionService } from "../../infrastructure/services/session"
import { FranchiseService } from "../../infrastructure/services/api/franchise"
import CreateFranchiseForm from "../../presentation/component/CreateFranchiseForm"
import { FranchiseBriefResponse } from "../../infrastructure/models/transport/response/franchiseBrief"
import { TYPES } from "../models/types"
import { COLORS } from "../constants/colors"
import { useSearch } from "../layouts/SideBarLayout"
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { SupportRole } from "../../infrastructure/models/common/supportrole"
import UpdateFranchiseForm from "../../presentation/component/UpdateFranchiseForm"
import { UpdateFranchise } from "../../infrastructure/models/transport/request/UpdateFranchise"
import { Franchise } from "../../infrastructure/models/common/franchise"

const Dashboard: React.FC = () => {
  const sessionService = container.resolve<SessionService>("sessionService")
  const franchiseService = container.resolve<FranchiseService>("franchiseService")
  const logger = container.resolve<Logger>("logger")
  const navigate = useNavigate()
  const { searchQuery } = useSearch()
  /**
   * Memoized user data from session storage.
   */
  const user = useMemo(() => sessionService.user, [sessionService.user])

  /**
   * Memoized list of franchises from session storage.
   */
  const initialFranchises = useMemo(
    () => sessionService.franchiseBrief || [],
    [sessionService.franchiseBrief]
  )

  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [apiLoading, setApiLoading] = useState(false)
  const [franchises, setFranchises] = useState<FranchiseBriefResponse[]>(initialFranchises)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [franchiseToDelete, setFranchiseToDelete] = useState<FranchiseBriefResponse | null>(null)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [franchiseToEdit, setFranchiseToEdit] = useState<Franchise | null>(null)
  const currentUser = sessionService.user
  const isSuperAdmin = currentUser?.role === SupportRole.SUPERADMIN
  const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false)

  /**
   * Fetch franchises when the component mounts or when user data changes.
   */
  useEffect(() => {
    if (!user) {
      logger.warn("No user data found in sessionStorage.")
      setError("No user data found. Please log in again.")
      navigate("/login")
    } else if (franchises.length === 0) {
      setError("No franchises assigned to the user.")
    }
  }, [user, franchises, logger, navigate])

  /**
   * Handles navigation to the franchise details page.
   * @param {string} franchiseId - The ID of the selected franchise.
   */
  const handleFranchiseClick = async (franchiseId: string) => {
    setLoading(true)
    try {
      const fetchedFranchise = await franchiseService.getFranchise(franchiseId)
      sessionService.setCurrentFranchise(fetchedFranchise)

      navigate(`/franchise/${franchiseId}`)
    } catch (error) {
      logger.error("Error fetching franchise:", error)
      sessionService.setCurrentFranchise(null)
      alert("Failed to fetch franchise. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handles form submission for creating a new terminal.
   * @param data - Terminal data to be created.
   */
  const handleSubmitCreateFranchise = async (data: any) => {
    setApiLoading(true)
    try {
      const franchise = await franchiseService.createFranchise(data)
      if (franchise) {
        sessionService.addFranchise(franchise)

        const briefFranchise: FranchiseBriefResponse = {
          franchiseId: franchise.franchiseId,
          name: franchise.name,
          address: `${franchise.address.street}, ${franchise.address.city}, ${franchise.address.state} - ${franchise.address.postalCode}`,// assuming address is an object and you need fullAddress as string
          status: franchise.status,
          owner: franchise.owner
          // include other fields that are part of FranchiseBriefResponse
        }
        // Also update the brief franchise list used on the dashboard
        const updatedBriefs = [...sessionService.franchiseBrief, briefFranchise] // Assuming franchise is of type FranchiseBriefResponse
        sessionService.setFranchisesBrief(updatedBriefs)

        setFranchises(updatedBriefs)
        setOpen(false)
        navigate("/dashboard") // Ensure redirection after successful creation

      }
    } catch (error: any) {
      console.log("FULL AXIOS ERROR:", error)

      const apiData = error?.response?.data || error

      const errorCode = apiData?.code
      const message = apiData?.message

      // DUPLICATE OWNER
      if (errorCode === "DU01") {
        setError("OWNER_ALREADY_EXISTS")
        return
      }

      // OTHER BACKEND ERRORS
      if (message) {
        setError(message)
        return
      }

      // FALLBACK
      setError("Something went wrong. Please try again.")
    } finally {
      setApiLoading(false)
    }
  }

  // Filter Franchises based on search query
  const filteredFranchises = franchises.filter(({ name, address, status }) =>
    `${name} ${address} ${status}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  )

  const handleConfirmDelete = async () => {
    if (!franchiseToDelete) return

    setApiLoading(true)
    try {
      await franchiseService.deleteFranchise(franchiseToDelete.franchiseId)

      // Remove from state and session
      const updatedFranchises = franchises.filter(f => f.franchiseId !== franchiseToDelete.franchiseId)
      setFranchises(updatedFranchises)
      sessionService.setFranchisesBrief(updatedFranchises)

      setSuccessModalOpen(true)
    } catch (error) {
      logger.error("Failed to delete franchise", error)
      alert("Failed to delete franchise. Please try again.")
    } finally {
      setApiLoading(false)
      setDeleteConfirmOpen(false)
      setFranchiseToDelete(null)
    }
  }

  const handleSubmitUpdateFranchise = async (data: UpdateFranchise) => {
    if (!franchiseToEdit) return

    setApiLoading(true)

    try {
      const updatePayload: UpdateFranchise = {
        franchiseId: franchiseToEdit.franchiseId,
      }

      // Only add fields if user changed them
      if (data.name) {
        updatePayload.name =
          data.name.charAt(0).toUpperCase() + data.name.slice(1).toLowerCase()
      }

      // Only add fields if user changed them
      if (data.status) {
        updatePayload.status =
          data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase()
      }

      if (data.terminalPaymentUpdates) {
        updatePayload.terminalPaymentUpdates = data.terminalPaymentUpdates
      }

      if (data.smsTemplates && Object.keys(data.smsTemplates).length > 0) {
        updatePayload.smsTemplates = data.smsTemplates
      }

      if (data.emailTemplates && Object.keys(data.emailTemplates).length > 0) {
        updatePayload.emailTemplates = data.emailTemplates
      }

      if (data.paymentProvider) {
        updatePayload.paymentProvider = data.paymentProvider
      }

      if (data.excludeOrdersPercentage !== undefined) {
        updatePayload.excludeOrdersPercentage = data.excludeOrdersPercentage
      }

      if (data.paymentDeductionValue !== undefined) {
        updatePayload.paymentDeductionValue = data.paymentDeductionValue
      }

      console.log("Sending Update Payload:", updatePayload)

      const updatedFranchise = await franchiseService.updateFranchise(updatePayload)

      const brief: FranchiseBriefResponse = {
        franchiseId: updatedFranchise.franchiseId,
        name: updatedFranchise.name,
        address: `${updatedFranchise.address.street}, ${updatedFranchise.address.city}, ${updatedFranchise.address.state} - ${updatedFranchise.address.postalCode}`,
        status: updatedFranchise.status,
        owner: updatedFranchise.owner,
      }

      const updatedFranchises = franchises.map(f =>
        f.franchiseId === brief.franchiseId ? brief : f
      )

      setFranchises(updatedFranchises)
      sessionService.setFranchisesBrief(updatedFranchises)

      setEditOpen(false)
      setFranchiseToEdit(null)
      setUpdateSuccessOpen(true)
    } catch (error: any) {
      console.error("Error while updating franchise:", error)
      logger.error("Failed to update franchise", error)

      const message =
        error?.response?.data?.message ||
        error.message ||
        "Update failed"

      alert(`Update failed. ${message}`)
    } finally {
      setApiLoading(false)
    }
  }

  useEffect(() => {
    if (updateSuccessOpen || successModalOpen) {
      const timer = setTimeout(() => {
        setUpdateSuccessOpen(false)
        setSuccessModalOpen(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [updateSuccessOpen, setSuccessModalOpen])

  return (
    <div className="flex flex-col p-10 pt-4" >
      {loading && (
        <div className="absolute inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
          <div className="text-white text-lg font-semibold">Loading....</div>
        </div>
      )}

      <div className="flex-1">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2
            data-automation-id="dashboard-lists-of-terminal-text"
            id="dashboard-lists-of-terminal-text"
            className="text-2xl font-semibold text-gray-800"
          >
            List of Franchises
          </h2>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen(true)}
            disabled={apiLoading}
          >
            Create Franchise
          </Button>
        </div>

        {filteredFranchises.length === 0 ? (
          <p
            data-automation-id="dashboard-no-terminal-available-text"
            id="dashboard-no-terminal-available-text"
            className="text-center text-gray-500"
          >
            No Franchise Available.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredFranchises.map(({ franchiseId, name, address, status }) => (
              <div
                key={franchiseId}
                onClick={() => handleFranchiseClick(franchiseId)}
                className="bg-white p-4 rounded-lg shadow-md text-left cursor-pointer hover:shadow-lg transition border border-gray-200 flex flex-col h-full"
              >
                <h3 className="text-xl font-semibold text-gray-900">{name}</h3>
                <p className="text-sm text-gray-500 mb-2">
                  <strong>Franchise ID:</strong> {franchiseId}
                </p>
                <div className="flex items-center text-gray-500 mb-2">
                  {/* Location icon */}
                  <span className="text-sm text-gray-700">{address}</span>
                  {/* Address next to the icon */}
                </div>
                <div className="mt-auto">
                  <span
                    className="px-3 py-1 text-xs font-medium rounded-full text-white"
                    style={{
                      backgroundColor:
                        status.toLowerCase() === TYPES.ACTIVE
                          ? COLORS.ACTIVE
                          : status.toLowerCase() === TYPES.INACTIVE
                            ? COLORS.INACTIVE
                            : COLORS.MAINTENANCE,
                    }}
                  >
                    {status}
                  </span>
                </div>

                <div className="flex justify-end mt-2 gap-1">
                  {[SupportRole.SUPERADMIN, SupportRole.ADMIN].includes(user?.role) && (
                    <IconButton
                      onClick={async (e) => {
                        e.stopPropagation()
                        try {
                          const fullFranchise = await franchiseService.getFranchise(franchiseId)
                          setFranchiseToEdit(fullFranchise)
                          setEditOpen(true)
                        } catch (error) {
                          console.error("Unable to fetch franchise for edit", error)
                        }
                      }}
                    >
                      <EditOutlinedIcon sx={{ color: COLORS.BLUE, fontSize: 22 }} />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Franchise</DialogTitle>
        <DialogContent>
          {apiLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <CreateFranchiseForm onSubmit={handleSubmitCreateFranchise} apiError={error} />
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            color="secondary"
            disabled={apiLoading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Franchise Modal */}
      {editOpen && franchiseToEdit && (
        <Dialog open onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Update Franchise</DialogTitle>
          <DialogContent>
            <UpdateFranchiseForm
              franchise={franchiseToEdit}
              onClose={() => {
                setEditOpen(false)
                setFranchiseToEdit(null)
              }}
              onSubmit={handleSubmitUpdateFranchise}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setEditOpen(false)} color="secondary" disabled={apiLoading}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={updateSuccessOpen}
        onClose={() => setUpdateSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: "#4CAF50",
              margin: "0 auto 16px auto"
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-8 h-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="white"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Franchise Details Updated Successfully.
          </Typography>
        </DialogContent>
      </Dialog>
    </div >
  )
}

export default Dashboard